import type { EnterpriseRiskRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type {
  Enterprise_Risk_Bool_Exp,
  GetEnterpriseRisksFlatQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetEnterpriseRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useGraphqlVariables = () => {
  const where = useEntityWhereFilter<Enterprise_Risk_Bool_Exp>(
    Parent_Type_Enum.EnterpriseRisk
  );

  return { where };
};

type UseGetEnterpriseRisksRegisterArgs = Record<string, never>;

export const useGetEnterpriseRisksRegister = createQueryHook<
  UseGetEnterpriseRisksRegisterArgs,
  EnterpriseRiskRegisterResponseRow[],
  GetEnterpriseRisksFlatQuery,
  // COI-770: V3 stores a single averaged median where V2 stored the raw
  // `FLOAT[2]` pair — see `collapseMedian` in the event-consumer's
  // enterprise-risk-score adaptor, whose comment records that V2 sent the array
  // and Hasura coerced it. So these four are a real V2/V3 divergence in the
  // VALUE shape, not a dropped field, and restoring the array is a scoring
  // change plus a backfill rather than a read fix. Declared here so the rest of
  // the register's selection stays enforced.
  | 'enterprise_risk.score.InherentScoreMedian'
  | 'enterprise_risk.score.ResidualScoreMedian'
  | 'enterprise_risk.score.InherentRatingMedian'
  | 'enterprise_risk.score.ResidualRatingMedian'
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.enterpriseRisk.register.queryOptions(),
  mapTrpcDataToGraphQL: (data) => ({ enterprise_risk: data }),
  graphqlDocument: GetEnterpriseRisksFlatDocument,
  graphqlVariables: useGraphqlVariables,
});

/**
 * Maps TRPC enterprise risks data to match the GraphQL query structure
 * If Possible Use the hook directly instead.
 */
export function mapTrpcEnterpriseRisksToGraphQL(
  trpcData: EnterpriseRiskRegisterResponseRow[] | undefined
):
  | StrictMappedGraphQLSelection<
      GetEnterpriseRisksFlatQuery,
      // COI-770 — see the note on the hook's gap list above.
      | 'enterprise_risk.score.InherentScoreMedian'
      | 'enterprise_risk.score.ResidualScoreMedian'
      | 'enterprise_risk.score.InherentRatingMedian'
      | 'enterprise_risk.score.ResidualRatingMedian'
    >
  | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    enterprise_risk: trpcData,
  };
}
